import { useState } from 'react';
import { Bell, Calendar, ChevronRight, User as UserIcon, Menu } from 'lucide-react';
import { useNavigate, Link } from "react-router-dom";

export default function Header({ currentUser, currentView, setView, onLogout, onToggleSidebar }) {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // Dynamic Current Date Formatting
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // 1. Updated view map to support structured nested array paths
  const breadcrumbMaps = {
    dashboard: ['Dashboard Overview'],
    section: ['Class Section'],
    profile: ['My Profile Card'],
    users: ['User Management'],
  };

  // Get path chunks for current view, falling back to an array fallback if unknown
  const activePaths = breadcrumbMaps[currentView] || [currentView || 'Overview'];

  // Customized notifications based on role
  const studentNotifications = [
    { id: 1, text: 'Submit assignment: Red-Black Trees due in 5 days', type: 'warning' },
    { id: 2, text: 'Your score for DBMS Assignment 3 is graded: 95/100', type: 'success' },
    { id: 3, text: 'Announcements: Midterm Grading guidelines published', type: 'info' }
  ];

  const facultyNotifications = [
    { id: 1, text: '3 student submissions require grading for Project Milestone 2', type: 'warning' },
    { id: 2, text: 'Midterm grade submission deadline is approaching (July 20)', type: 'danger' },
    { id: 3, text: 'Global System Update completed: Upgraded network bandwidth', type: 'info' }
  ];

  const notifications = currentUser?.role === 'student' ? studentNotifications : facultyNotifications;

  return (
    <header id="header-container" className="h-16 font-dynamic-title bg-white border-b border-slate-200/80 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
      
      {/* Left: Sidebar Toggle Menu + Dynamic Multi-level Breadcrumbs */}
      <div className="flex items-center gap-3 text-slate-500 text-xs select-none">
        {/* Toggle Sidebar Action Button (Visible on mobile/tablet viewports) */}
        <button 
          onClick={onToggleSidebar}
          className="p-1.5 hover:bg-slate-50 text-slate-500 hover:text-slate-800 rounded-lg transition-colors cursor-pointer lg:hidden"
          title="Toggle Navigation Menu"
        >
          <Menu className="h-4.5 w-4.5" />
        </button>

        <span 
          className="font-medium hover:text-slate-900 cursor-pointer transition-colors" 
          onClick={() => navigate("/dashboard")}
        >
          Home
        </span>
        
        {activePaths.map((segment, index) => {
          const isLast = index === activePaths.length - 1;
          return (
            <div key={index} className="flex items-center gap-2">
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
              <span 
                className={`capitalize tracking-wide ${
                  isLast 
                    ? 'font-bold text-slate-800' 
                    : 'font-medium text-slate-500'
                }`}
              >
                {segment}
              </span>
            </div>
          );
        })}
      </div>

      {/* Right: Actions list */}
      <div className="flex items-center gap-4">
        
        {/* Dynamic Date Visual */}
        <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200/60 rounded-lg text-xs font-semibold text-slate-600">
          <Calendar className="h-4 w-4 text-slate-400" />
          <span>{currentDate}</span>
        </div>

        {/* Alerts Bell */}
        <div className="relative">
          <button
            id="notifications-bell"
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowProfileMenu(false);
            }}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all relative cursor-pointer"
          >
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 h-2 w-2 bg-emerald-400 rounded-full animate-pulse"></span>
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-2.5 w-80 bg-white border border-slate-200 shadow-xl rounded-xl py-2 z-40">
              <div className="px-4 py-2 border-b border-slate-100 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-800">Alert Notifications</span>
                <span className="text-[10px] bg-sky-50 text-sky-600 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                  {notifications.length} New
                </span>
              </div>
              <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {notifications.map((notif) => (
                  <div key={notif.id} className="p-3 hover:bg-slate-50 text-xs text-slate-600 leading-relaxed">
                    <div className="flex items-start gap-2">
                      <span className={`w-1.5 h-1.5 mt-1.5 rounded-full shrink-0 ${
                        notif.type === 'warning' ? 'bg-amber-500' :
                        notif.type === 'danger' ? 'bg-rose-500' :
                        notif.type === 'success' ? 'bg-emerald-500' : 'bg-sky-500'
                      }`} />
                      <span>{notif.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}